import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, DropTarget } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import _isNull from 'lodash/isNull';
import { ruleType } from 'types';
import SelectableList from 'components/SelectableList';

import CreateRuleGroupOrRuleModal from 'scenes/Rules/components/CreateRuleGroupOrRuleModal';
import DeleteRuleGroupModal from 'scenes/Rules/components/DeleteRuleGroupModal';
import RuleTemplate from './components/RuleTemplate';

import './style.scss';

const cardTarget = {
  canDrop() {
    return true;
  },

  // needs when we have few items and dragging to the end of the list
  drop(props, monitor) {
    const drop = monitor.getDropResult();
    const model = !_isNull(drop)
      ? drop.model
      : [{ ...props.rules[props.rules.length - 1], last: true }];
    return { model };
  },
};

const RuleList = ({
  searchTerm,
  count,
  rules,
  fullGroups,
  selectedRule,
  currentRuleIndex,
  isReordering,
  matchedRulesMap,
  getRuleMatchedRows,
  selectRule,
  deselectRule,
  toggleRuleStatus,
  reorderGroup,
  reorderRule,
  onOpenDuplicateRuleModal,
  onOpenAddRuleModal,
  onCloseAddRuleModal,
  groupCount,
  isAddNewRuleModalOpen,
  onSaveNewRule,
  deleteRuleGroup,
  selectedRuleGroup,
  selectRuleGroupItem,
  ruleGroupItem,
  isDeleting,
  isDeleteGroupModalOpen,
  onOpenDeleteModal,
  onCloseDeleteModal,
  groupName,
  moveRuleToGroup,
  groupOrder,
  orderList,
  partialUpdateGroup,
  partialUpdateRule,
  connectDropTarget,
}) =>
  connectDropTarget(
    <div styleName="list">
      <SelectableList
        count={count}
        currentItemIndex={currentRuleIndex}
        data-test="rules-list"
        deselectItem={deselectRule}
        getRuleMatchedRows={getRuleMatchedRows}
        isReordering={isReordering}
        ItemComponent={
          <RuleTemplate
            fullGroups={fullGroups}
            isAddNewRuleModalOpen={isAddNewRuleModalOpen}
            moveRuleToGroup={moveRuleToGroup}
            partialUpdateGroup={partialUpdateGroup}
            partialUpdateRule={partialUpdateRule}
            reorderGroupItem={reorderRule}
            searchTerm={searchTerm}
            selectedGroupItem={ruleGroupItem}
            selectGroupItem={selectRuleGroupItem}
            onCloseAddRuleModal={onCloseAddRuleModal}
            onOpenAddRuleModal={onOpenAddRuleModal}
            onOpenDeleteModal={onOpenDeleteModal}
            onOpenDuplicateModal={onOpenDuplicateRuleModal}
          />
        }
        items={rules}
        matchedRulesMap={matchedRulesMap}
        reorderItem={reorderGroup}
        scrollableClassName="concepts-lists"
        selectedItem={selectedRule}
        selectedItemGroup={selectedRuleGroup}
        selectItem={selectRule}
        toggleItemStatus={toggleRuleStatus}
      />

      {isAddNewRuleModalOpen && (
        <CreateRuleGroupOrRuleModal
          isGrouped
          count={groupCount}
          groupOrder={groupOrder}
          modelName="rule"
          orderList={orderList}
          onClose={onCloseAddRuleModal}
          onSubmit={onSaveNewRule}
        />
      )}

      {isDeleteGroupModalOpen && (
        <DeleteRuleGroupModal
          isFetching={isDeleting}
          name={groupName}
          onCloseRequest={onCloseDeleteModal}
          onSubmit={deleteRuleGroup}
        />
      )}
    </div>,
  );

RuleList.propTypes = {
  count: PropTypes.number.isRequired,
  currentRuleIndex: PropTypes.number.isRequired,
  deleteRuleGroup: PropTypes.func.isRequired,
  deselectRule: PropTypes.func.isRequired,
  getRuleMatchedRows: PropTypes.func.isRequired,
  isAddNewRuleModalOpen: PropTypes.bool.isRequired,
  isReordering: PropTypes.bool.isRequired,
  matchedRulesMap: PropTypes.shape({}).isRequired,
  reorderGroup: PropTypes.func.isRequired,
  reorderRule: PropTypes.func.isRequired,
  rules: PropTypes.arrayOf(ruleType).isRequired,
  selectRule: PropTypes.func.isRequired,
  selectRuleGroupItem: PropTypes.func.isRequired,
  toggleRuleStatus: PropTypes.func.isRequired,
  onCloseAddRuleModal: PropTypes.func.isRequired,
  onCloseDeleteModal: PropTypes.func.isRequired,
  onOpenAddRuleModal: PropTypes.func.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
  onOpenDuplicateRuleModal: PropTypes.func.isRequired,
  onSaveNewRule: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func,
  fullGroups: PropTypes.arrayOf(ruleType),
  groupCount: PropTypes.number,
  groupName: PropTypes.string,
  groupOrder: PropTypes.number,
  isDeleteGroupModalOpen: PropTypes.bool,
  isDeleting: PropTypes.bool,
  moveRuleToGroup: PropTypes.func,
  orderList: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      order: PropTypes.string,
    }),
  ),
  partialUpdateGroup: PropTypes.func,
  partialUpdateRule: PropTypes.func,
  ruleGroupItem: ruleType,
  selectedRule: ruleType,
  selectedRuleGroup: ruleType,
};

export default DragDropContext(HTML5Backend)(
  DropTarget('DRAG_RULE', cardTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    canDrop: monitor.canDrop(),
    getDropResult: monitor.getDropResult(),
  }))(RuleList),
);
